import { useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { MathSubject } from '@shared/mathSubjects';
import { useFocusTransition } from '@/contexts/focusTransitionContext';
import {
  mathSubjectFocusApi,
  type MathSubjectFocusResponse,
} from '@/services/mathSubjectFocusApi';

import { useAuthenticatedCall } from './useAuthenticatedCall';

export interface UseMathSubjectFocusResult {
  /** True only when this student can meaningfully choose — see the gate below. */
  enabled: boolean;
  /** Initial fetch in flight. */
  isLoading: boolean;
  /** Any applicability read, including a level-triggered revalidation. */
  isFetching: boolean;
  /** True only for non-placeholder applicability data at the current key. */
  isReady: boolean;
  /** Applicability-read failure; focus writes expose their error globally. */
  error: unknown;
  /** Retry the current applicability read. */
  refetch: () => unknown;
  /** Server write in flight; dependent controls must stay blocked. */
  isUpdating: boolean;
  /** Current focus. `null` = Both (today's interleaved behaviour). */
  value: MathSubject | null;
  /** Write the focus. Published only after the server accepts it. */
  setValue: (next: MathSubject | null) => void;
}

export const MATH_SUBJECT_FOCUS_QUERY_KEY = [
  'practice',
  'focus-subject',
] as const;

/**
 * The one visibility gate for the focus control, spelled once so the two
 * callers can't drift: the student's level must actually have an E-Math /
 * A-Math split, **and** they must be enrolled in both.
 *
 * Deliberately not the length-only check `/progress` used to carry — that one
 * would offer the toggle to a mis-provisioned G1 student holding two subject
 * tags, whose syllabus has no split to focus. `applicable` is
 * `isMathSubjectApplicable(examLevel, orgLevel)`: the server evaluates it for
 * the actor themselves, and the staff-viewing-a-student path on `/progress`
 * evaluates it in-page against the *viewed* student's level.
 *
 * Everyone who fails the gate never renders the control and resolves to
 * `null` — including a student whose stored focus is stale because their
 * enrolment changed, so a dropped subject can never silently keep filtering.
 */
export function mathSubjectFocusEnabled(
  applicable: boolean,
  mathSubjects: readonly string[] | null | undefined
): boolean {
  return applicable && (mathSubjects?.length ?? 0) > 1;
}

/**
 * Reads and writes the student's durable E-Math / A-Math focus.
 *
 * React-query backed on purpose: the provider is mounted per-page rather than
 * once in a global shell, and the shared cache is what keeps several mounted
 * instances consistent. Flipping the focus on `/practice` and walking to
 * `/progress` shows the same value with no refetch, and an accepted write
 * lands in every observer at once.
 *
 * Consumers should read this through `useMathSubjectFocusContext`; the hook
 * itself is called only by `MathSubjectFocusProvider`.
 */
export interface UseMathSubjectFocusOptions {
  /** Cache identity for applicability after a level switch. */
  levelFocusKey?: string | null;
  /** Hold the applicability read until the enclosing level has resolved. */
  enabled?: boolean;
}

export function useMathSubjectFocus({
  levelFocusKey,
  enabled: queryEnabled = true,
}: UseMathSubjectFocusOptions = {}): UseMathSubjectFocusResult {
  const { isAuthenticated, user } = useAuth0();
  const { callWithAuth } = useAuthenticatedCall();
  const queryClient = useQueryClient();
  const transition = useFocusTransition();
  const retryMutationRef = useRef<(next: MathSubject | null) => void>(
    () => undefined
  );

  // Scoped per authenticated user so a sign-out/sign-in never inherits the
  // previous account's focus out of a warm cache.
  const queryKey = useMemo(
    () => [
      ...MATH_SUBJECT_FOCUS_QUERY_KEY,
      user?.sub ?? null,
      ...(levelFocusKey ? [levelFocusKey] : []),
    ],
    [user?.sub, levelFocusKey]
  );

  const query = useQuery<MathSubjectFocusResponse>({
    queryKey,
    enabled: isAuthenticated && queryEnabled,
    // Enrolment and level change about once a term; a page-to-page walk should
    // not re-ask. Any write updates the cache directly.
    staleTime: 5 * 60 * 1000,
    // A level change keeps the selector frame stable while its new
    // applicability record loads. Consumers never treat this placeholder as
    // permission to fetch destination content.
    placeholderData: keepPreviousData,
    queryFn: () => callWithAuth(token => mathSubjectFocusApi.get(token)),
  });

  const { mutate, isPending } = useMutation<
    { focusMathSubject: MathSubject | null },
    unknown,
    MathSubject | null,
    { transitionId: number }
  >({
    // Calls with the same scope run serially. The tabs are disabled while one
    // is pending, but this also protects programmatic callers and a same-frame
    // double click before React can paint the disabled state.
    scope: { id: `student-focus:${user?.sub ?? 'anonymous'}` },
    // A stale focus GET may already be in flight (for example, a window-focus
    // refetch). Cancel its cache publication before writing so a response that
    // read the old server value cannot land after this PATCH succeeds and roll
    // the accepted focus back. This deliberately does not publish `next`.
    onMutate: async next => {
      const transitionId = transition.begin({
        kind: 'subject',
        destinationLabel: next ?? 'Both subjects',
        target: { mathSubject: next },
        retry: () => retryMutationRef.current(next),
      });
      await queryClient.cancelQueries({ queryKey, exact: true });
      await queryClient.cancelQueries({
        queryKey: ['practice'],
        predicate: candidate =>
          !MATH_SUBJECT_FOCUS_QUERY_KEY.every(
            (part, index) => candidate.queryKey[index] === part
          ) &&
          !['practice', 'focus-level'].every(
            (part, index) => candidate.queryKey[index] === part
          ),
      });
      return { transitionId };
    },
    mutationFn: (next: MathSubject | null) =>
      callWithAuth(token => mathSubjectFocusApi.update(next, token)),
    onSuccess: (data, _next, context) => {
      // Publish the accepted preference into every cached applicability
      // record for this user. Applicability is level-specific, but the stored
      // subject preference is not; History deliberately has no level provider
      // and must still observe a switch made on Practice/Progress.
      queryClient.setQueriesData<MathSubjectFocusResponse>(
        {
          queryKey: [...MATH_SUBJECT_FOCUS_QUERY_KEY, user?.sub ?? null],
        },
        previous =>
          previous
            ? { ...previous, focusMathSubject: data.focusMathSubject }
            : previous
      );
      // Every downstream practice read is focus-sensitive. The PATCH response
      // is already authoritative for this focus query, though: refetching it
      // here could overlap the next scoped PATCH and publish an older focus.
      void queryClient.invalidateQueries({
        queryKey: ['practice'],
        refetchType: 'none',
        predicate: candidate =>
          !MATH_SUBJECT_FOCUS_QUERY_KEY.every(
            (part, index) => candidate.queryKey[index] === part
          ),
      });
      transition.accept(context?.transitionId ?? transition.id);
    },
    // A rejection commonly means enrolment or level changed behind this
    // client. Keep the old value authoritative, then refresh the exact focus
    // record (and its visibility gate) before another scoped write may run.
    onError: (error, _next, context) => {
      transition.failSave(context?.transitionId ?? transition.id, error);
      void queryClient.invalidateQueries({ queryKey, exact: true });
    },
  });

  retryMutationRef.current = mutate;

  const setValue = useCallback(
    (next: MathSubject | null) => {
      mutate(next);
    },
    [mutate]
  );

  const focusEnabled = mathSubjectFocusEnabled(
    query.data?.applicable ?? false,
    query.data?.mathSubjects
  );

  return {
    enabled: focusEnabled,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isReady: query.data !== undefined && query.isPlaceholderData !== true,
    error: query.error,
    refetch: query.refetch,
    isUpdating: isPending,
    value: focusEnabled ? (query.data?.focusMathSubject ?? null) : null,
    setValue,
  };
}
